/**
 * Lightweight, privacy-respecting funnel tracker.
 * Posts to the public /api/events/track endpoint. No PII sent.
 * Session id is a UUID stored in localStorage so repeat visits in the same
 * browser aggregate correctly without server-side cookies.
 *
 * NOTE: Keep this file framework-agnostic — the only React dependency is the
 * hook at the bottom, which is optional.
 */
import { useEffect, useRef } from "react";
import { API } from "./api";

const SESSION_KEY = "skillgap_sid";
const ATTR_KEY = "skillgap_attr";

function getSessionId() {
  try {
    let sid = localStorage.getItem(SESSION_KEY);
    if (!sid) {
      sid = (crypto?.randomUUID?.() || `s_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`);
      localStorage.setItem(SESSION_KEY, sid);
    }
    return sid;
  } catch {
    return `s_${Date.now()}`;
  }
}

function readUtmParams() {
  try {
    const p = new URLSearchParams(window.location.search);
    return {
      source: p.get("utm_source") || null,
      medium: p.get("utm_medium") || null,
      campaign: p.get("utm_campaign") || null,
      term: p.get("utm_term") || null,
      content: p.get("utm_content") || null,
    };
  } catch {
    return { source: null, medium: null, campaign: null, term: null, content: null };
  }
}

/**
 * First-touch attribution. Captured once per browser (localStorage) so the
 * utm_source that first brought the user in is preserved even when they
 * come back directly to sign up. Last-touch is still available via
 * readUtmParams() if we ever need it.
 */
export function captureAttribution() {
  try {
    const existing = JSON.parse(localStorage.getItem(ATTR_KEY) || "null");
    if (existing && existing.utm_source) return existing;  // first-touch wins
    const u = readUtmParams();
    const anyUtm = u.source || u.medium || u.campaign || u.term || u.content;
    // Only persist if there is at least one UTM param OR an external referrer.
    const ref = (typeof document !== "undefined" && document.referrer) ? document.referrer : "";
    const sameOrigin = ref && (() => {
      try { return new URL(ref).host === window.location.host; } catch { return false; }
    })();
    if (!anyUtm && (!ref || sameOrigin)) return null;
    const attr = {
      utm_source: u.source,
      utm_medium: u.medium,
      utm_campaign: u.campaign,
      utm_term: u.term,
      utm_content: u.content,
      landing_path: typeof window !== "undefined" ? window.location.pathname : "",
      referrer: (ref && !sameOrigin) ? ref.slice(0, 400) : null,
      captured_at: new Date().toISOString(),
    };
    localStorage.setItem(ATTR_KEY, JSON.stringify(attr));
    return attr;
  } catch {
    return null;
  }
}

export function getAttribution() {
  try {
    return JSON.parse(localStorage.getItem(ATTR_KEY) || "null");
  } catch {
    return null;
  }
}

/**
 * Fire a funnel event. Non-blocking; swallows network errors (we don't want
 * analytics to break the user experience).
 * @param {string} event_type  one of the server-whitelisted FUNNEL_STAGES, or `custom_*`
 * @param {object} metadata    optional small key/value map (no PII)
 */
export async function track(event_type, metadata) {
  try {
    const utm = readUtmParams();
    const body = {
      event_type,
      session_id: getSessionId(),
      path: typeof window !== "undefined" ? window.location.pathname : "",
      source: utm.source,
      medium: utm.medium,
      campaign: utm.campaign,
      metadata: metadata || null,
    };
    // sendBeacon survives page unload — perfect for "cta_click" that navigates away.
    // We stay on same origin (API_URL === window.location.origin + /api) so no CORS preflight.
    if (
      typeof navigator !== "undefined" &&
      typeof navigator.sendBeacon === "function" &&
      ["cta_click", "checkout_opened"].includes(event_type)
    ) {
      const blob = new Blob([JSON.stringify(body)], { type: "application/json" });
      const ok = navigator.sendBeacon(`${API}/events/track`, blob);
      if (ok) return;
    }
    await fetch(`${API}/events/track`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
      keepalive: true,
    });
  } catch {
    /* intentionally silent */
  }
}

/**
 * React helper — fires `event_type` exactly once when the component mounts.
 * Guards against StrictMode double-invocation.
 */
export function usePageEvent(event_type, metadata) {
  const fired = useRef(false);
  useEffect(() => {
    if (fired.current) return;
    fired.current = true;
    track(event_type, metadata);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
